"use client";

import { api } from "@v1/backend/convex/_generated/api";
import type { Id } from "@v1/backend/convex/_generated/dataModel";
import { cn } from "@v1/ui/utils";
import { useQuery } from "convex/react";
import { Loader2, Paperclip } from "lucide-react";
import { useTranslations } from "next-intl";
import { type ReactElement, useEffect, useState } from "react";
import { toast } from "sonner";
import { useMessengerSurface } from "./messenger-context";

/** Fills the tab opened on click (popup blockers) with the file, then unmounts. */
function FileOpener({
  taskId,
  commentId,
  fileId,
  tab,
  onDone,
}: {
  readonly taskId: Id<"tasks">;
  readonly commentId: Id<"taskComments">;
  readonly fileId: Id<"files">;
  readonly tab: Window | null;
  readonly onDone: () => void;
}): null {
  const t = useTranslations("messenger.thread");
  const surface = useMessengerSurface();
  const file = useQuery(api.customerChat.messenger_thread.getTicketFile, {
    surface,
    task_id: taskId,
    comment_id: commentId,
    file_id: fileId,
  });
  useEffect(() => {
    if (file === undefined) return;
    if (file === null || file.url === null) {
      tab?.close();
      toast.error(t("dateiNichtVerfuegbar"));
    } else if (tab !== null) {
      tab.location.replace(file.url);
    } else {
      window.open(file.url, "_blank", "noopener,noreferrer");
    }
    onDone();
  }, [file, onDone, t, tab]);
  return null;
}

export function MessengerAttachmentChips({
  taskId,
  commentId,
  attachments,
  isSelf,
}: {
  readonly taskId: Id<"tasks">;
  readonly commentId: Id<"taskComments">;
  readonly attachments: ReadonlyArray<{ readonly file_id: Id<"files">; readonly name: string }>;
  readonly isSelf: boolean;
}): ReactElement {
  const [opening, setOpening] = useState<{ readonly fileId: Id<"files">; readonly tab: Window | null } | null>(
    null,
  );
  const open = (fileId: Id<"files">): void => {
    const tab = window.open("about:blank", "_blank");
    if (tab !== null) tab.opener = null;
    setOpening({ fileId, tab });
  };
  return (
    <ul className="mt-2 flex flex-col items-stretch gap-1">
      {attachments.map((a) => (
        <li key={a.file_id}>
          <button
            type="button"
            onClick={() => open(a.file_id)}
            className={cn(
              "inline-flex min-h-7 max-w-full items-center gap-1.5 rounded-full px-3 text-[11.5px] leading-5 transition-colors",
              isSelf
                ? "bg-white/15 text-white hover:bg-white/25"
                : "bg-action-500/[0.08] text-primary/70 hover:bg-action-500/[0.12]",
            )}
          >
            {opening?.fileId === a.file_id ? (
              <Loader2 className="size-3.5 shrink-0 animate-spin" aria-hidden />
            ) : (
              <Paperclip className="size-3.5 shrink-0" strokeWidth={1.75} aria-hidden />
            )}
            <span className="min-w-0 truncate">{a.name}</span>
          </button>
        </li>
      ))}
      {opening !== null ? (
        <FileOpener
          taskId={taskId}
          commentId={commentId}
          fileId={opening.fileId}
          tab={opening.tab}
          onDone={() => setOpening(null)}
        />
      ) : null}
    </ul>
  );
}
